/*
===============================================================================================================
                                 Copyright 2026-2026, Blue Yonder Group, Inc.
                                              All Rights Reserved

                                THIS IS UNPUBLISHED PROPRIETARY SOURCE CODE OF
                                            BLUE YONDER GROUP, INC.

                            The copyright notice above does not evidence any actual
                                 or intended publication of such source code.

===============================================================================================================
*/

import React, { useContext, useEffect } from 'react';
import PropTypes from 'prop-types';
import { ChangesContext } from '@/contexts/changes-context';
import ErrorReportingHOC from '@/components/error-reporting-hoc';

/**
 * GroupRequiredValidator
 * A hidden validator that enforces at least one item from a group of checkboxes is selected.
 * Integrates with ErrorReportingHOC/ValidationContext to block form submission when invalid.
 *
 * @param {string[]} valueIds - List of valueIds (from ChangesContext) to check
 * @param {function} setContent - Injected by ErrorReportingHOC; call with a truthy value when valid
 * @param {string|null} error - Injected by ErrorReportingHOC; non-null when validation fails
 */
export function GroupRequiredValidatorInner({ valueIds, setContent, error }) {
  const { changes } = useContext(ChangesContext);

  useEffect(() => {
    const anySelected = valueIds.some(
      (id) => changes?.find((changeObject) => changeObject.valueId === id && changeObject.value),
    );
    setContent(anySelected ? 'selected' : null);
  }, [changes]);

  if (!error) return null;

  return (
    <>
      <div className="group-required-error">{error}</div>
      <style jsx>
        {`
          .group-required-error {
            color: var(--color-error-red, #d0021b);
            font-size: var(--font-size-H3);
            margin-top: 4px;
          }
        `}
      </style>
    </>
  );
}

GroupRequiredValidatorInner.propTypes = {
  valueIds: PropTypes.arrayOf(PropTypes.string).isRequired,
  setContent: PropTypes.func.isRequired,
  error: PropTypes.string,
};

GroupRequiredValidatorInner.defaultProps = {
  error: null,
};

export default ErrorReportingHOC(GroupRequiredValidatorInner);
